import { ContaAzulInfo } from 'models/AccountsPayable'
import { constructorCustomer, CustomerParams } from 'models/Customers'
import { constructorDiscounts, DiscountParams } from 'models/Discounts'
import { constructorMerchant, MerchantParams } from 'models/Merchant'
import { NfseScheduled, IssueMode, MonthOffset } from 'models/NFe'
import { constructorPayment, PaymentParams } from 'models/Payment'
import { PaymentTypeDetails } from 'models/PaymentTypeDetails'
import { Products } from 'models/Plans'

import {
  constructorMessageTracking,
  MessageTrackingParams
} from './message_tracking'
import { constructorNotes, NotesParams } from './notes'

export const amountTypeEnum = {
  amount_paid: 'Valor pago',
  amount_issued: 'Valor original',
  amount_custom: 'Valor customizado'
}

export const natureOperationEnum = {
  tributacao_no_municipio: 'Trib. no Município',
  tributacao_fora_do_municipio: 'Trib. Fora do Município',
  isento: 'Isento',
  imune: 'Imune',
  suspensa_por_decisao_judicial: 'Susp. Decisão Judicial',
  suspensa_por_decisao_administrativa: 'Susp. Decisão Adm'
}

export const issueWhenEnum = {
  on_billing_issued: 'Quando a fatura abrir para pagamento',
  on_billing_paid: 'Quando o pagamento for efetuado',
  on_before_due_date: 'Antes do vencimento',
  on_after_due_date: 'Depois do vencimento',
  custom_schedule: 'Dia específico do mês'
}

export const frequencyEnum = {
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semester: 'Semestral',
  annual: 'Anual'
}
interface BillingDocuments {
  billingUuid: string
  createdAt: string
  description: string
  docFileName: string
  docFileSize: string
  docFileUrl: string
  docType: string
  updatedAt: string
  uuid: string
}

interface Historic {
  event: string
  description: string
  date_change: string
  user: string
}

export interface CreditCardCharges {
  status: string
  description: string
  chargedAt: string
  statusDetail?: string
}

export interface CancellationReason {
  cancellableType: string
  countAsChurn: boolean
  createdAt: string
  notes: string | null
  reasonType:
    | 'other'
    | 'wrong_billing'
    | 'service_cancellation'
    | 'issue_error'
    | 'non_payment'
  updatedAt: string
  uuid: string
}

export interface Negativation {
  days_to_report: number
  // SC-11742: avisos antes da negativação (notificações extras da régua). Só
  // vem no detalhe da cobrança com negativação agendada.
  warnings?: {
    count: number
    next_at: string | null
    next_channels: Array<'email' | 'sms' | 'whats'>
  }
  negativation: {
    uuid: string
    status: string
    status_detail: any
    created_at: string
    updated_at: string
    customer_uuid: string
    merchant_uuid: string
    billing_uuid: string
  }
}

export interface InvoiceItem {
  uuid: string
  amount_billed: number
  quantity: number
  amount_unit: number
  product?: Pick<Products, 'amount' | 'name' | 'uuid' | 'totalCycles' | 'tags'>
  description?: string
  tags?: { uuid?: string; id?: string; name: string; color?: string }[]
  start_cycle?: number
  total_cycles?: number
  // Bonificação por item: abatimento incondicional que reduz o amount_billed
  // da cobrança — ver Billing#calc_amount_billed no BASE.
  bonus_policy?: 'no_bonus' | 'fixed' | 'percentage'
  bonus_amount?: number | string
  // Classificação MRR x avulsa calculada no backend (BillingItem#metric_kind),
  // a mesma regra do MRR das Métricas. Ver getItemTagFromProduct.
  metric_kind?: 'mrr' | 'avulsa'
}

export interface InvoiceParams {
  amountBilled: string
  amountPaid: string
  amountUpdated: number
  antecipated: boolean
  createdAt: string
  customer: CustomerParams
  customerUuid: string
  datePaid: string
  description: string
  dirty: boolean
  discountAmount: string
  discountDays: number
  discountPolicy: string
  discounts: DiscountParams[]
  dueDate: string
  expirationField: string | number
  interestPolicy: string
  logoUrl: string
  merchant: MerchantParams
  merchantUuid: string
  paymentType: string
  payments: PaymentParams[]
  recurrenceCycle: number
  status: string
  issued: boolean
  updatedAt: string
  uuid: string
  recurrenceUuid: string
  smsInvoiceStatus: string
  emailInvoiceStatus: string
  notes: NotesParams[]
  messageTracking: MessageTrackingParams[]
  viewed: boolean
  /** SC-10890: total de aberturas. Ausente = backend anterior à feature. */
  viewsCount?: number
  /** SC-10890: última abertura conhecida (ISO). */
  lastViewedAt?: string | null
  billingDocuments: BillingDocuments[]
  historic: Historic[]
  creditCardCharges: CreditCardCharges[]
  canManualRetry?: boolean
  manualRetryReason?: string
  cancellationReason: CancellationReason
  interestFee: string
  interestFine: string
  nfsesScheduled?: NfseScheduled
  cancelableNfses?: Array<{
    uuid: string
    nfse_number: string | null
    status: string
  }>
  nfse_issuer_uuid?: string
  nfse_uuid?: string
  nfsePolicy?: string
  nfseAutomationActive?: boolean
  linkedNfse?: any
  canLinkNfse?: boolean
  nfses?: Array<{
    uuid: string
    nfse_number: string | null
    status: string
    nfse_item: {
      amount: number | string | null
      created_at: string | null
    } | null
  }>
  metadata: any
  contaAzulInfo?: ContaAzulInfo | null
  negativation: Negativation
  items: InvoiceItem[]
  issue_days?: number
  issue_billing_type?: 'days_before_due_date' | 'specific_day'
  issue_date?: string
  // New fields for interval type configuration
  interval_type?: string // Legacy field - not used in current implementation
  interval_value?: number
  // New fields for specific day NF configuration
  issue_mode?: IssueMode
  specific_day?: number // 1-31
  specific_month_offset?: MonthOffset
  due_date_period?: string // For backward compatibility
  hasBonus?: boolean
  bonusSettings?: {
    cyclesCount: number
    type: 'value' | 'percentage'
    amount: string
  }
  // Saldo de bonificação de ciclos anteriores abatido nesta cobrança (SC-11262).
  bonusCarryover?: {
    amount: number
    status: 'planned' | 'applied'
    general_amount: number
    items_amount: number
  } | null
  tags?: string
  paymentTypeDetails?: PaymentTypeDetails
  recurrence?: any
  // Cartão vinculado à cobrança. `source` distingue a origem do vínculo:
  // 'recurrence' e 'installment' são vínculo ativo (vai ser cobrado de novo),
  // 'charged' é só o cartão com que a fatura já foi paga.
  linkedCreditCard?: {
    card_brand?: string
    last_4_digits?: string
    source?: 'recurrence' | 'installment' | 'charged'
    linked_at?: string
    last_charge_failed?: boolean
  } | null
  couponUsage?: {
    uuid: string
    couponUuid: string
    couponCode: string
    discountType: string
    discountValue: number
    appliedDiscountAmount: number
    removedAt: string | null
    createdAt: string
  } | null
  chargedCreditCard?: ChargedCreditCard | null
}

export interface ChargedCreditCard {
  uuid: string
  first4Digits: string
  last4Digits: string
  holderName: string
  expirationMonth: number
  expirationYear: number
  cardBrand: string
  createdAt: string
  isSaved: boolean
}

function constructorBillingDocuments(data: any) {
  return data.map((billing: any) => ({
    billingUuid: billing.billing_uuid,
    createdAt: billing.created_at,
    description: billing.description,
    docFileName: billing.doc_file_name,
    docFileSize: billing.doc_file_size,
    docFileUrl: billing.doc_file_url,
    docType: billing.doc_type,
    updatedAt: billing.updated_at,
    uuid: billing.uuid
  }))
}

export function constructorCancellationReason(data: any) {
  return {
    uuid: data?.uuid,
    cancellableType: data?.cancellable_type,
    reasonType: data?.reason_type,
    notes: data?.notes,
    countAsChurn: data?.count_in_metrics,
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  }
}

function constructorCreditCardCharges(data: any) {
  return data.map((creditCardCharge: any) => ({
    status: creditCardCharge.status,
    description: creditCardCharge.description,
    chargedAt: creditCardCharge.created_at,
    statusDetail: creditCardCharge.status_detail
  }))
}

export function constructorNfseScheduled(data: NfseScheduled) {
  return {
    ...data
  } as NfseScheduled
}

export function constructorInvoice(data: any) {
  return {
    amountBilled: data?.amount_billed,
    amountPaid: data?.amount_paid,
    amountUpdated: data?.amount_updated,
    antecipated: data?.antecipated,
    createdAt: data?.created_at,
    customer: constructorCustomer(data?.customer),
    customerUuid: data?.customer_uuid,
    datePaid: data?.date_paid,
    description: data?.description,
    dirty: data?.dirty,
    discountAmount: data?.discount_amount,
    discountDays: data?.discount_days,
    discountPolicy: data?.discount_policy,
    discounts: constructorDiscounts(data?.discounts),
    dueDate: data?.due_date,
    expirationField: data?.expiration_date,
    interestPolicy: data?.interest_policy,
    logoUrl: data?.logo_url,
    merchant: constructorMerchant(data?.merchant),
    merchantUuid: data?.merchant_uuid,
    paymentType: data?.payment_type,
    payments: constructorPayment(data?.payments),
    recurrenceCycle: data?.recurrence_cycle,
    status: data?.status,
    issued: data?.issued,
    updatedAt: data?.updated_at,
    uuid: data?.uuid,
    recurrenceUuid: data?.recurrence_uuid,
    emailInvoiceStatus: data?.email_invoice_status,
    smsInvoiceStatus: data?.sms_invoice_status,
    notes: constructorNotes(data?.notes),
    messageTracking: constructorMessageTracking(data?.message_tracking),
    // Coerção num lugar só: a coluna é boolean no banco, mas registros antigos
    // e respostas legadas podem trazer 0/1.
    viewed: !!data?.viewed,
    viewsCount: data?.open_count,
    lastViewedAt: data?.last_opened_at,
    billingDocuments: constructorBillingDocuments(data?.billing_documents),
    historic: data?.historic,
    cancellationReason: constructorCancellationReason(
      data?.cancellation_reason
    ),
    creditCardCharges: constructorCreditCardCharges(data?.credit_card_charges),
    canManualRetry: data?.can_manual_retry,
    manualRetryReason: data?.manual_retry_reason,
    interestFee: data?.interest_fee || '1.0',
    interestFine: data?.interest_fine || '2.0',
    nfsesScheduled: data?.nfse_scheduled
      ? constructorNfseScheduled(data?.nfse_scheduled)
      : undefined,
    cancelableNfses: Array.isArray(data?.nfses)
      ? data.nfses
          .filter((n: { status?: string }) => n?.status === 'confirmed')
          .map((n: { uuid: string; nfse_number: string | null }) => ({
            uuid: n.uuid,
            nfse_number: n.nfse_number,
            status: 'confirmed'
          }))
      : undefined,
    nfse_issuer_uuid: data?.nfse_issuer_uuid,
    nfse_uuid: data?.nfse_uuid,
    nfsePolicy: data?.nfse_policy,
    nfseAutomationActive: data?.nfse_automation_active,
    linkedNfse: data?.linked_nfse,
    canLinkNfse: data?.can_link_nfse,
    nfses: data?.nfses,
    metadata: data?.metadata,
    negativation: data?.negativation,
    recurrence: data?.recurrence,
    bonusCarryover: data?.bonus_carryover ?? null,
    // Processar dados de bonificação
    hasBonus: data?.bonus_policy !== 'no_bonus',
    bonusSettings:
      data?.bonus_policy !== 'no_bonus' && data?.bonus
        ? {
            cyclesCount: data.bonus.total_cycles,
            type: data?.bonus_policy === 'fixed' ? 'value' : 'percentage',
            amount: data.bonus.amount.toString()
          }
        : undefined,
    items: data.billing_items?.map((item: any) => ({
      uuid: item.uuid,
      quantity: Number(item.quantity),
      ...(item.product
        ? {
            product: {
              ...item.product,
              tags: item.product.tags || []
            }
          }
        : item.description
        ? { description: item.description }
        : {}),
      amount_billed: item.amount_billed,
      amount_unit: item.amount_unit,
      tags: item.tags,
      start_cycle: item.start_cycle,
      total_cycles: item.total_cycles,
      bonus_policy: item.bonus_policy,
      bonus_amount: item.bonus_amount,
      metric_kind: item.metric_kind
    })),

    paymentTypeDetails: data?.payment_type_details,
    linkedCreditCard: data?.linked_credit_card,

    issue_days: data?.issue_days,
    issue_billing_type: data?.issue_billing_type,
    issue_date: data?.issue_date,
    // Map new interval type configuration fields
    interval_type: data?.interval_type,
    interval_value: data?.interval_value,
    // Map new NF configuration fields
    issue_mode: data?.issue_mode,
    specific_day: data?.specific_day,
    specific_month_offset: data?.specific_month_offset,
    due_date_period: data?.due_date_period,
    tags: data?.tags,
    couponUsage: (() => {
      const usage = data?.coupon_usages?.find((u: any) => !u.removed_at)
      return usage
        ? {
            uuid: usage.uuid,
            couponUuid: usage.coupon?.uuid,
            couponCode: usage.coupon?.code,
            discountType: usage.discount_type_snapshot,
            discountValue: usage.discount_value_snapshot,
            appliedDiscountAmount: usage.applied_discount_amount,
            removedAt: usage.removed_at,
            createdAt: usage.created_at
          }
        : null
    })(),
    chargedCreditCard: data?.charged_credit_card
      ? {
          uuid: data.charged_credit_card.uuid,
          first4Digits: data.charged_credit_card.first_4_digits,
          last4Digits: data.charged_credit_card.last_4_digits,
          holderName: data.charged_credit_card.holder_name,
          expirationMonth: data.charged_credit_card.expiration_month,
          expirationYear: data.charged_credit_card.expiration_year,
          cardBrand: data.charged_credit_card.card_brand,
          createdAt: data.charged_credit_card.created_at,
          isSaved: data.charged_credit_card.is_saved
        }
      : null,
    contaAzulInfo: data?.conta_azul_info
      ? {
          uuid: data.conta_azul_info.uuid,
          status: data.conta_azul_info.status,
          caExternalId: data.conta_azul_info.ca_external || null,
          caProtocolId: data.conta_azul_info.ca_protocol || null,
          caInstallmentId: data.conta_azul_info.ca_installment || null,
          caBaixaId: data.conta_azul_info.ca_baixa || null,
          lastError: data.conta_azul_info.last_error || null,
          syncedAt: data.conta_azul_info.synced_at || null
        }
      : null
  } as InvoiceParams
}
